// Taşeron portalı — Panel (api-contract §28.3 `GET /supplier/dashboard`). Aktif firma +
// çağıranın kendi taşeronu (kimlik token/bağdan — istemci GÖNDERMEZ). Salt-okuma projeksiyon;
// hiçbir düzeltme burada YAPILMAZ — yollar §15.2 (personel belgesi), §13.6 (taşeron belgesi),
// §17.1 (personelin kendi eğitimi) ve §21.2 (personel atama)'dır; bu sayfa yalnız oraya
// YÖNLENDİRİR (bkz. MissingItemsCard/UpcomingPermitsCard/AwaitingAnalysisCard).
//
// **Panel rebuild** — owner mevcut tasarımı beğenmedi; backend §28 AYNEN KALDI, yalnız
// SUNUM değişti. "7 Günlük Görünüm" (RiskWeekCard) ve "Sizden bekleyenler"
// (PendingActionsCard, kişiye göre gruplu) TAMAMEN KALKTI. Yeni düzen:
//   1. Başlık — Breadcrumb + `PageHeader` (S37-3; firma adı artık kabukta, başlıkta DEĞİL).
//   2. `contractor.status`/`compliance.overall` rozetleri (eski "Aktif"/"Eksik" çifti) owner
//      kararıyla başlıktan KALDIRILDI (bu rebuild'in ikinci turu) — bu sinyal artık portalda
//      HİÇBİR ekranda görünmüyor. Bu bilinçli bir karar; "unutulmuş" sanıp rastgele geri
//      EKLENMEZ — geri istenirse owner'dan ayrı bir talep gerekir. `query.data.contractor.
//      status`/`compliance` alanları DTO'da hâlâ VAR (§28.3), yalnız bu ekranda basılmıyor.
//   3. Grid: sol sütun (geniş, `lg:col-span-2`) sırayla `UpcomingPermitsCard` (TABLO sunumu —
//      owner A/B karşılaştırmasını 2026-08-16'da bu kazandı, bkz. o dosyanın başlık notu) →
//      `MissingItemsCard` (TÜM `items`, kişi gruplaması YOK); sağ sütun sırayla
//      `SiteReadinessCard` (§28.3 `counts` — partition kanıtı kendi dosya başı notunda) →
//      `AwaitingAnalysisCard` (§20.3 — `PendingAnalysesStrip` ile AYNI veri, panonun akranı).
//      `MissingItemsCard` owner isteğiyle (2026-08-16, bu rebuild'in altıncı turu) sağ
//      sütundan SOL sütuna, izin tablosunun ALTINA taşındı — sağ sütun artık SOL'dan daha
//      KISA, bu BEKLENEN bir sonuç, doldurma İÇERİK EKLENMEZ.
//   4. `AppInfoCard` (mobil uygulama paneli) owner kararıyla GİZLENDİ (bu rebuild'in
//      dördüncü turu, 2026-08-14) — `SHOW_APP_INFO_CARD` bayrağına bkz. Kod bilinçli olarak
//      SİLİNMEDİ (geri açmak `true` yeterli); NAV'daki "Mobil Uygulama" girişi ve
//      `/mobile-app` sayfası bundan ETKİLENMEZ, yalnız panodaki kart gizli.

import {
  Breadcrumb,
  Card,
  CardContent,
  PageHeader,
  Skeleton,
  Spinner,
  currentCulture,
  supplierDashboardApi,
  useAuthStore,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { ClipboardCheck, FileText, Smartphone, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AppDownloadLinks } from '../components/AppDownloadLinks';
import { ContractorStateNotice } from '../components/ContractorStateNotice';
import { AwaitingAnalysisCard } from '../components/dashboard/AwaitingAnalysisCard';
import { MissingItemsCard } from '../components/dashboard/MissingItemsCard';
import { DashboardPanel, PanelHeader } from '../components/dashboard/PanelHeader';
import { SiteReadinessCard } from '../components/dashboard/SiteReadinessCard';
import { UpcomingPermitsCard } from '../components/dashboard/UpcomingPermitsCard';
import { supplierDashboardKeys } from '../lib/supplierDashboardKeys';
import { useAppSettings } from '../lib/useAppSettings';

/** Owner isteği (2026-08-14) — pano mobil uygulama paneli GİZLİ. Kod bilinçli olarak
 *  duruyor: geri açmak için `true` yeterli, bileşen ve `useAppSettings` bağlantısı sağlam.
 *  NAV'daki "Mobil Uygulama" girişi ve `/mobile-app` sayfası bundan ETKİLENMEZ. */
const SHOW_APP_INFO_CARD = false;

export function DashboardPage() {
  const { t } = useTranslation();
  const culture = currentCulture();
  const companyId = useAuthStore((s) => s.principal?.companyId ?? null);

  const query = useQuery({
    queryKey: supplierDashboardKeys.dashboard(companyId),
    queryFn: () => supplierDashboardApi.getSupplierDashboard(),
  });

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb items={[{ label: t('dashboard.title') }]} />
      {/* S37-3: firma adı başlıktan kalktı — kabukta (CompanySwitcher) zaten görünür (S1). */}
      <PageHeader title={t('dashboard.title')} helperText={t('dashboard.subtitle')} />

      {query.isPending ? (
        <DashboardSkeleton />
      ) : query.isError ? (
        <ContractorStateNotice error={query.error} onRetry={() => query.refetch()} />
      ) : query.data ? (
        <div className="flex flex-col gap-6">
          {/* S37-3 (seçenek A, expert paritesi): 2+1 `xl`'den; altında tek sütun. Aralık Carbon
              `gap-6` (eski owner `gap-4` kararı S37 ile değişti). */}
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            {/* Sol sütun — `MissingItemsCard` owner isteğiyle (2026-08-16 altıncı tur) izin
                tablosunun ALTINA taşındı (dosya başı notu, madde 3). */}
            <div className="flex flex-col gap-6 xl:col-span-2">
              <UpcomingPermitsCard dto={query.data} culture={culture} />
              <MissingItemsCard dto={query.data} companyId={companyId} />
            </div>
            <div className="grid grid-cols-1 content-start gap-6 sm:grid-cols-2 xl:grid-cols-1">
              <SiteReadinessCard dto={query.data} />
              <AwaitingAnalysisCard />
            </div>
          </div>
          {/* Mobil uygulama bilgisi (§26) — ambiyans bilgisi, işlemsel değil; bu yüzden
              grid'in ALTINDA. NAV'daki "Mobil Uygulama" girişiyle aynı kaynaktan
              (`GET /app-settings`); link boşsa "yakında". Owner kararıyla GİZLİ — bkz.
              `SHOW_APP_INFO_CARD` (dosya başı notu). */}
          {SHOW_APP_INFO_CARD ? <AppInfoCard /> : null}
        </div>
      ) : null}
    </div>
  );
}

/** Yüklenirken panel iskeleti — gerçek başlıklarla (yükseklik zıplamasın), gövde `Skeleton`. */
function DashboardSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3" aria-busy="true">
      <output className="sr-only">{t('dashboard.loading')}</output>
      <div className="flex flex-col gap-6 xl:col-span-2">
        <DashboardPanel>
          <PanelHeader icon={ClipboardCheck} title={t('dashboard.permits.title')} />
          <div className="flex flex-col gap-3 px-5 py-4">
            <Skeleton className="h-4" />
            <Skeleton className="h-4" />
            <Skeleton className="h-4" />
          </div>
        </DashboardPanel>
        <DashboardPanel>
          <PanelHeader icon={FileText} title={t('dashboard.missing.title')} />
          <div className="flex flex-col gap-3 px-5 py-4">
            <Skeleton className="h-4" />
            <Skeleton className="h-4" />
          </div>
        </DashboardPanel>
      </div>
      <DashboardPanel className="self-start">
        <PanelHeader icon={Users} title={t('dashboard.readiness.title')} />
        <div className="flex flex-col gap-3 px-5 py-4">
          <Skeleton />
          <Skeleton className="w-2/3" />
        </div>
      </DashboardPanel>
    </div>
  );
}

/** Anasayfa mobil uygulama kartı — iOS/Android mağaza linkleri (§26). */
function AppInfoCard() {
  const { t } = useTranslation();
  const { iosAppUrl, androidAppUrl, isLoading } = useAppSettings();

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-5">
        <div className="flex items-center gap-2">
          <Smartphone className="h-5 w-5 text-primary" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-fg">{t('dashboard.app.title')}</h3>
        </div>
        <p className="text-sm text-muted">{t('dashboard.app.body')}</p>
        {isLoading ? (
          <div className="flex min-h-[3rem] items-center">
            <Spinner label={t('appDownload.loading')} />
          </div>
        ) : (
          <AppDownloadLinks iosAppUrl={iosAppUrl} androidAppUrl={androidAppUrl} />
        )}
      </CardContent>
    </Card>
  );
}
